import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { FiArrowLeft, FiShare2, FiThumbsUp, FiMessageCircle, FiClock, FiUser } from 'react-icons/fi';
import { getBlogPost, incrementPostViewCount, getBlogPostsByCategory, updateBlogPost } from '../services/firebase/blogService';
import CommentsSection from '../components/blog/CommentsSection';
import './BlogDetailPage.css';

const BlogDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [liked, setLiked] = useState(false);

  // Fetch blog post and related posts
  useEffect(() => {
    const fetchPost = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch the main post
        const postResult = await getBlogPost(id);
        if (postResult.success) {
          const postData = postResult.data;
          setPost(postData);

          // Increment view count
          await incrementPostViewCount(id);

          // Fetch related posts from same category
          if (postData.category) {
            const relatedResult = await getBlogPostsByCategory(postData.category);
            if (relatedResult.success) {
              // Filter out current post and limit to 3
              const filtered = relatedResult.data
                .filter(p => p.id !== id)
                .slice(0, 3);
              setRelatedPosts(filtered);
            }
          }
        } else {
          setError('Blog post not found');
        }
      } catch (err) {
        console.error('Error fetching blog post:', err);
        setError('Failed to load blog post');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchPost();
    }
  }, [id]);

  // Handle like button
  const handleLike = async () => {
    if (!post) return;
    
    try {
      const newLikeCount = liked ? post.likes - 1 : post.likes + 1;
      await updateBlogPost(id, { likes: newLikeCount });
      setPost({ ...post, likes: newLikeCount });
      setLiked(!liked);
    } catch (err) {
      console.error('Error updating likes:', err);
    }
  };

  // Handle share
  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: post?.title,
        text: post?.excerpt,
        url: window.location.href
      }).catch(err => console.log('Share error:', err));
    } else {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(window.location.href);
      alert('Link copied to clipboard!');
    }
  };

  // Calculate reading time
  const calculateReadingTime = (content) => {
    if (!content) return 0;
    const wordsPerMinute = 200;
    const wordCount = content.trim().split(/\s+/).length;
    return Math.ceil(wordCount / wordsPerMinute);
  };

  if (loading) {
    return (
      <div className="blog-detail-page">
        <div className="container mx-auto px-4 py-8">
          <div className="flex items-center justify-center min-h-screen">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-orange-100 mb-4">
                <div className="w-8 h-8 border-3 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
              </div>
              <p className="text-gray-600">Loading article...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="blog-detail-page">
        <div className="container mx-auto px-4 py-8">
          <div className="flex flex-col items-center justify-center min-h-screen">
            <div className="text-center">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Article Not Found</h2>
              <p className="text-gray-600 mb-6">{error || 'The blog post you are looking for does not exist.'}</p>
              <button
                onClick={() => navigate('/blog')}
                className="inline-flex items-center gap-2 bg-orange-500 text-white px-6 py-3 rounded-lg hover:bg-orange-600 transition"
              >
                <FiArrowLeft /> Back to Blog
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const readingTime = calculateReadingTime(post.content);

  return (
    <div className="blog-detail-page">
      {/* Back Button */}
      <div className="container mx-auto px-4 py-6">
        <button
          onClick={() => navigate('/blog')}
          className="inline-flex items-center gap-2 text-orange-500 hover:text-orange-600 font-semibold mb-6"
        >
          <FiArrowLeft size={20} /> Back to Blog
        </button>
      </div>

      {/* Featured Image */}
      {post.featuredImage && (
        <div className="blog-featured-image mb-8">
          <img
            src={post.featuredImage}
            alt={post.title}
            className="w-full h-96 object-cover"
          />
        </div>
      )}

      {/* Main Content */}
      <article className="blog-article container mx-auto px-4 py-8 max-w-4xl">
        {/* Category Badge */}
        {post.category && (
          <div className="mb-4">
            <span className="inline-block bg-orange-100 text-orange-700 px-3 py-1 rounded-full text-sm font-medium capitalize">
              {post.category}
            </span>
          </div>
        )}

        {/* Title */}
        <h1 className="blog-title text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
          {post.title}
        </h1>

        {/* Meta Information */}
        <div className="blog-meta flex flex-col md:flex-row md:items-center gap-4 pb-6 border-b border-gray-200 mb-8">
          {/* Author */}
          <div className="flex items-center gap-3">
            {post.vendorAvatar && (
              <img
                src={post.vendorAvatar}
                alt={post.vendorName}
                className="w-10 h-10 rounded-full object-cover"
              />
            )}
            <div>
              <p className="font-semibold text-gray-900">{post.vendorName || 'Anonymous'}</p>
              <p className="text-sm text-gray-500">Vendor</p>
            </div>
          </div>

          {/* Date & Reading Time */}
          <div className="flex flex-wrap gap-6 text-sm text-gray-600">
            <div className="flex items-center gap-2">
              <FiUser size={16} className="text-orange-500" />
              <span>{new Date(post.createdAt?.toDate?.() || post.createdAt).toLocaleDateString()}</span>
            </div>
            <div className="flex items-center gap-2">
              <FiClock size={16} className="text-orange-500" />
              <span>{readingTime} min read</span>
            </div>
            <div className="flex items-center gap-2">
              <FiMessageCircle size={16} className="text-orange-500" />
              <span>{post.viewCount || 0} views</span>
            </div>
          </div>
        </div>

        {/* Article Content */}
        <div className="blog-content prose prose-lg max-w-none mb-8">
          <p className="text-lg text-gray-700 leading-relaxed mb-6">{post.excerpt}</p>
          <div className="text-gray-800 leading-relaxed whitespace-pre-wrap">
            {post.content}
          </div>
        </div>

        {/* Tags */}
        {post.tags && post.tags.length > 0 && (
          <div className="blog-tags mb-8 pb-8 border-b border-gray-200">
            <h3 className="text-sm font-semibold text-gray-600 uppercase mb-3">Tags</h3>
            <div className="flex flex-wrap gap-2">
              {post.tags.map((tag, index) => (
                <span
                  key={index}
                  className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm hover:bg-gray-200 transition cursor-pointer"
                >
                  #{tag}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="blog-actions flex flex-wrap gap-4 mb-8 pb-8 border-b border-gray-200">
          <button
            onClick={handleLike}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition ${
              liked
                ? 'bg-red-100 text-red-600'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <FiThumbsUp size={18} />
            {post.likes || 0} Likes
          </button>
          <button
            onClick={handleShare}
            className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 transition"
          >
            <FiShare2 size={18} />
            Share
          </button>
        </div>
      </article>

      {/* Comments Section */}
      <section className="blog-comments py-8 bg-white">
        <div className="container mx-auto px-4 max-w-4xl">
          <CommentsSection postId={id} />
        </div>
      </section>

      {/* Related Articles Section */}
      {relatedPosts.length > 0 && (
        <section className="blog-related py-12 bg-gray-50">
          <div className="container mx-auto px-4 max-w-4xl">
            <h2 className="text-3xl font-bold text-gray-900 mb-8">Related Articles</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {relatedPosts.map((relPost) => (
                <Link
                  key={relPost.id}
                  to={`/blog/${relPost.id}`}
                  className="blog-card bg-white rounded-lg overflow-hidden shadow hover:shadow-lg transition"
                >
                  {relPost.featuredImage && (
                    <img
                      src={relPost.featuredImage}
                      alt={relPost.title}
                      className="w-full h-40 object-cover group-hover:scale-105 transition"
                    />
                  )}
                  <div className="p-4">
                    <p className="text-orange-500 text-sm font-semibold mb-2 capitalize">
                      {relPost.category}
                    </p>
                    <h3 className="font-bold text-gray-900 mb-2 line-clamp-2">
                      {relPost.title}
                    </h3>
                    <p className="text-gray-600 text-sm line-clamp-2 mb-4">
                      {relPost.excerpt}
                    </p>
                    <div className="flex items-center justify-between text-xs text-gray-500">
                      <span>{relPost.vendorName}</span>
                      <span>{relPost.viewCount || 0} views</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="blog-cta py-12 bg-orange-500 text-white">
        <div className="container mx-auto px-4 max-w-4xl text-center">
          <h2 className="text-3xl font-bold mb-4">Enjoyed this article?</h2>
          <p className="text-orange-100 mb-6 text-lg">
            Browse more articles or create your own content
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/blog"
              className="px-6 py-3 bg-white text-orange-500 font-bold rounded-lg hover:bg-gray-100 transition"
            >
              Read More Articles
            </Link>
            <Link
              to="/vendor/blog/create"
              className="px-6 py-3 bg-orange-600 text-white font-bold rounded-lg hover:bg-orange-700 transition border border-white"
            >
              Write an Article
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BlogDetailPage;
